import React, { useState, useMemo, useEffect, useCallback } from "react";
import XLSX from "xlsx-js-style";
import { applyRegisterStyle, writeFileWithPageSetup, fileDate } from "@/utils/excelStyle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import FinancialVisibilityGuard from "@/components/permissions/FinancialVisibilityGuard";
import { canViewFinancialDashboard } from "@/lib/permissions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { PDFPreviewModal } from "@/components/dialogs/PDFPreviewModal";
import { usePagination } from "@/lib/usePagination";
import {
  FileText,
  Receipt,
  Download,
  Printer,
  Loader2,
  Euro,
  CheckCircle2,
  Clock,
  AlertCircle,
  Pencil,
  MoreHorizontal,
  Filter,
  X,
  FileSpreadsheet,
  Search,
  Building2,
  Users,
  CalendarRange,
  ChevronDown,
  Eye,
  ArrowLeft,
  Trash2,
  Mail,
  ArrowRightLeft,
  Copy
} from "lucide-react";
import { currency, formatDate } from "@/utils/helpers";
import { toast } from "sonner";
import PaymentDialog from "@/components/dialogs/PaymentDialog";
import InvoiceDetail from "@/components/InvoiceDetail";
import { cn } from "@/lib/utils";
import { invoiceTaxableAmount } from "@/lib/practiceBilling";
import api from "@/api";

/**
 * Fatture Tab Component - Professional Table Layout
 * Shows invoices in a compact table with advanced filters
 */
export default function FattureTab({
  invoices,
  clients,
  cases,
  updateInvoiceStatus,
  exportInvoiceXML,
  onAddPayment,
  onRemovePayment,
  refreshInvoices,
  onEditInvoice,
  onDuplicateInvoice,
  onDeleteInvoice,
  settings,
  defaultManagementFilter = "agenzia",
  // 28/02/2026 — supporto Documento Proforma (gemello non-fiscale).
  // Quando documentType === "proforma" il tab mostra etichette e titoli
  // diversi e abilita il bottone "Nuovo Documento Proforma".
  documentType = "invoice",
  onCreateProforma,
  onCreateInvoice,
  isLoadingExternally = false,
  // 07/06/2026 — Callback post-conversione Proforma → Fattura. Quando
  // valorizzato, viene invocato con la NUOVA fattura subito dopo il
  // success del backend (e dopo `refreshInvoices`). Il caller decide
  // cosa fare: tipicamente switch al tab "Fatture Emesse" + apertura
  // immediata della fattura in modalita' edit per consentire all'operatore
  // di completare metodo di pagamento, scadenze, note senza dover
  // ricercare manualmente la fattura.
  onConversionSuccess,
  // 07/07/2026 — visibilità riepiloghi finanziari (P0 security refinement)
  operatorRole = "amministratore",
}) {
  // UI States
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [printingPdf, setPrintingPdf] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isPrintingList, setIsPrintingList] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  
  // Print state - React-managed instead of DOM manipulation
  const [printImages, setPrintImages] = useState([]);
  const [isPrintReady, setIsPrintReady] = useState(false);
  
  // Refs for lifecycle management
  const isMountedRef = React.useRef(true);
  const printAbortRef = React.useRef(false);
  
  // PDF Viewer Modal state (for print/view actions)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerBlob, setPdfViewerBlob] = useState(null);
  const [pdfViewerLoading, setPdfViewerLoading] = useState(false);
  const [pdfViewerError, setPdfViewerError] = useState(null);
  const [pdfViewerInvoice, setPdfViewerInvoice] = useState(null);

  // Email templates state (17/07/2026 — v1.4.1). Popolato una tantum
  // al mount: la lista è breve (< 10 elementi) e le API sono già cached
  // lato server. Il template scelto dall'operatore viene passato a
  // handleSendEmail via submenu. Fallback: se il fetch fallisce o la
  // lista è vuota, si usa il legacy `api.getEmailTemplate()`.
  const [emailTemplates, setEmailTemplates] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.getEmailTemplates()
      .then((list) => { if (!cancelled) setEmailTemplates(Array.isArray(list) ? list : []); })
      .catch((err) => { console.warn('[FattureTab] template fetch failed', err); });
    return () => { cancelled = true; };
  }, []);
  
  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      printAbortRef.current = true;
    };
  }, []);
  
  // Detail view state
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);

  // Filter States - Default to Gestione Agenzia
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [gestioneFilter, setGestioneFilter] = useState(defaultManagementFilter);
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // 11/06/2026 — Filter on documentType to separate invoices and credit
  // notes within the same "Fatture Emesse" register. "issued" = both,
  // "invoice" = only fatture, "credit_note" = only NdC.
  const [docTypeFilter, setDocTypeFilter] = useState("issued");

  // Enrich invoices with client data
  const enrichedInvoices = useMemo(() => {
    return invoices.map(inv => {
      const client = clients.find(c => c.id === inv.clientId);
      return {
        ...inv,
        clientName: client?.fullName || "Cliente non trovato",
        managementType: inv.managementType || client?.managementType || "studio",
        // Alcune fatture storiche sono composte interamente da righe
        // manuali: in quel caso `imponibile` principale è 0, ma la base
        // imponibile reale è nella somma di manualRows[].amount.
        taxableAmount: invoiceTaxableAmount(inv),
      };
    });
  }, [invoices, clients]);

  // Apply filters
  const filteredInvoices = useMemo(() => {
    return enrichedInvoices
      .filter(inv => {
        // Search query
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesSearch = 
            inv.invoiceNumber?.toLowerCase().includes(query) ||
            inv.clientName?.toLowerCase().includes(query) ||
            inv.notes?.toLowerCase().includes(query);
          if (!matchesSearch) return false;
        }

        // Date range
        if (dateFrom && inv.invoiceDate < dateFrom) return false;
        if (dateTo && inv.invoiceDate > dateTo) return false;

        // Client filter
        if (clientFilter !== "all" && inv.clientId !== clientFilter) return false;

        // Gestione filter
        if (gestioneFilter !== "all" && inv.managementType !== gestioneFilter) return false;

        // Year filter
        if (yearFilter !== "all") {
          const invYear = inv.invoiceDate ? inv.invoiceDate.substring(0, 4) : "";
          if (invYear !== yearFilter) return false;
        }

        // Payment status filter
        if (paymentStatusFilter !== "all") {
          const ps = inv.paymentStatus || "non_pagata";
          if (paymentStatusFilter !== ps) return false;
        }

        // Status filter
        if (statusFilter !== "all" && inv.status !== statusFilter) return false;

        // Document type filter (11/06/2026): only in "issued" mode this
        // matters. In proforma mode, all rows are proforma.
        if (documentType === "invoice" && docTypeFilter !== "issued") {
          const dt = inv.documentType || "invoice";
          if (docTypeFilter === "invoice" && dt !== "invoice") return false;
          if (docTypeFilter === "credit_note" && dt !== "credit_note") return false;
        }

        // Amount range
        if (amountFrom && (inv.totale || 0) < parseFloat(amountFrom)) return false;
        if (amountTo && (inv.totale || 0) > parseFloat(amountTo)) return false;

        return true;
      })
      // Sort by invoice date descending (newest first), then by invoice number descending
      .sort((a, b) => {
        const dateA = a.invoiceDate || a.issueDate || '';
        const dateB = b.invoiceDate || b.issueDate || '';
        const dateCompare = dateB.localeCompare(dateA);
        if (dateCompare !== 0) return dateCompare;
        // If same date, sort by invoice number descending
        const numA = a.invoiceNumber || '';
        const numB = b.invoiceNumber || '';
        return numB.localeCompare(numA, undefined, { numeric: true });
      });
  }, [enrichedInvoices, searchQuery, dateFrom, dateTo, clientFilter, gestioneFilter, yearFilter, paymentStatusFilter, statusFilter, amountFrom, amountTo, docTypeFilter, documentType]);

  // Calculate totals for filtered invoices.
  // 11/06/2026 — Le Note di Credito riducono il totale e il residuo:
  //   • totale: NdC sottratta (rappresentano storni, non incassi)
  //   • residuo: nessun contributo (NdC non sono credito da incassare)
  //   • pagato: nessun contributo
  // In questo modo i KPI (Totale, Incassato, Residuo) restano semanticamente
  // coerenti: una fattura completamente stornata da NdC azzera entrambi.
  const totals = useMemo(() => {
    return filteredInvoices.reduce((acc, inv) => {
      const isCN = inv.documentType === "credit_note";
      const totale = Number(inv.totale) || 0;
      const totalPaid = Number(inv.totalPaid) || 0;
      const credited = Number(inv.creditedAmount) || 0;
      // Effective due: per le fatture sottraiamo l'eventuale storno.
      // La NdC stessa non contribuisce al residuo.
      const effDue = isCN ? 0 : Math.max(0, totale - credited - totalPaid);
      return {
        imponibile: acc.imponibile + (isCN ? -inv.taxableAmount : inv.taxableAmount),
        iva: acc.iva + (isCN ? -(Number(inv.ivaAmount) || 0) : (Number(inv.ivaAmount) || 0)),
        totale: acc.totale + (isCN ? -totale : totale),
        pagato: acc.pagato + (isCN ? 0 : totalPaid),
        residuo: acc.residuo + effDue,
      };
    }, { imponibile: 0, iva: 0, totale: 0, pagato: 0, residuo: 0 });
  }, [filteredInvoices]);

  // Paginazione condivisa (20/02/2026).
  const {
    paged: pagedInvoices,
    paginationBar: invoicesPaginationBar,
  } = usePagination(filteredInvoices, {
    storageKey: "fatture",
    itemLabelSingular: "fattura",
    itemLabelPlural: "fatture",
  });

  // Check if any filter is active
  const hasActiveFilters = searchQuery || dateFrom || dateTo || clientFilter !== "all" || 
    gestioneFilter !== "all" || yearFilter !== String(new Date().getFullYear()) || paymentStatusFilter !== "all" || statusFilter !== "all" ||
    amountFrom || amountTo;

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setClientFilter("all");
    setGestioneFilter("all");
    setYearFilter(String(new Date().getFullYear()));
    setPaymentStatusFilter("all");
    setStatusFilter("all");
    setAmountFrom("");
    setAmountTo("");
  };

  // Unique clients for filter dropdown
  const uniqueClients = useMemo(() => {
    const clientIds = [...new Set(invoices.map(inv => inv.clientId))];
    return clientIds.map(id => clients.find(c => c.id === id)).filter(Boolean);
  }, [invoices, clients]);

  // Payment dialog handlers
  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentDialogOpen(true);
  };

  const handleAddPayment = async (invoiceId, payment) => {
    const updated = await onAddPayment(invoiceId, payment);
    if (updated) setSelectedInvoice(updated);
  };

  const handleRemovePayment = async (invoiceId, paymentIndex) => {
    const updated = await onRemovePayment(invoiceId, paymentIndex);
    if (updated) setSelectedInvoice(updated);
  };

  // Convert proforma → invoice handler (28/02/2026). Visible only in
  // proforma mode for documents not yet converted. Shows confirm, calls
  // API, on success refreshes the list and surfaces the new invoice
  // number. Idempotent: backend returns 409 if double-converted.
  const [convertingId, setConvertingId] = useState(null);
  const handleConvertToInvoice = async (proforma) => {
    if (!proforma?.id) return;
    if (proforma.convertedToInvoiceId) {
      toast.info(`Proforma gia' convertita nella fattura ${proforma.convertedToInvoiceNumber || ''}`);
      return;
    }
    const ok = window.confirm(
      `Convertire la proforma ${proforma.invoiceNumber || ''} in fattura ufficiale?\n\nVerra' generato un nuovo numero di fattura. La proforma resta archiviata come storica.`
    );
    if (!ok) return;
    setConvertingId(proforma.id);
    try {
      const newInvoice = await api.convertProformaToInvoice(proforma.id);
      toast.success(`Fattura ${newInvoice.invoiceNumber} creata dalla proforma. Apertura in modifica...`);
      if (typeof refreshInvoices === "function") {
        try { await refreshInvoices(); } catch { /* swallow */ }
      }
      // UX 07/06/2026: dopo la conversione, apri automaticamente la
      // fattura in modalita' edit per consentire all'operatore di
      // completare subito i dati senza doverla cercare in elenco.
      // Il caller (FatturazioneTab in modalita' proforma) si occupa di
      // switchare anche al tab "Fatture Emesse".
      if (typeof onConversionSuccess === "function") {
        try { onConversionSuccess(newInvoice); } catch (e) {
          console.warn("onConversionSuccess handler failed:", e);
        }
      } else if (typeof onEditInvoice === "function") {
        try { onEditInvoice(newInvoice); } catch (e) {
          console.warn("onEditInvoice fallback failed:", e);
        }
      }
    } catch (err) {
      const msg = err?.message || "Conversione fallita";
      toast.error(msg);
    } finally {
      setConvertingId(null);
    }
  };

  // Emit Credit Note handler (11/06/2026). Supports both full reversal
  // (default) and partial via prompt. The backend handles atomicity,
  // counter increment (unified with invoice sequence), cross-reference,
  // payment-status recompute and practice reopening if fully credited.
  const [issuingCnId, setIssuingCnId] = useState(null);
  const handleIssueCreditNote = async (invoice) => {
    if (!invoice || !invoice.id) return;
    const choiceFull = window.confirm(
      `Emettere Nota di Credito per la fattura ${invoice.invoiceNumber}?\n\n` +
      `Premi OK per uno STORNO INTEGRALE (€ ${(Number(invoice.totale) || 0).toFixed(2)}).\n` +
      `Premi Annulla per inserire un importo parziale.`
    );
    let payload = null;
    if (!choiceFull) {
      const raw = window.prompt(
        `Importo totale della Nota di Credito (in €):\n` +
        `Massimo stornabile: € ${(Number(invoice.totale) || 0).toFixed(2)}`,
        ""
      );
      if (raw === null || raw === "") return;
      const totale = Number(String(raw).replace(",", "."));
      if (!Number.isFinite(totale) || totale <= 0) {
        toast.error("Importo non valido.");
        return;
      }
      // Spalmiamo l'importo mantenendo la stessa proporzione IVA della fattura.
      const srcTot = Number(invoice.totale) || 0;
      const srcImp = invoiceTaxableAmount(invoice);
      const srcIvaAmt = Number(invoice.ivaAmount) || 0;
      const srcContrib = Number(invoice.contributoIntegrativo) || 0;
      const ratio = srcTot > 0 ? totale / srcTot : 0;
      payload = {
        imponibile: Number((srcImp * ratio).toFixed(2)),
        contributoIntegrativo: Number((srcContrib * ratio).toFixed(2)),
        iva: Number(invoice.iva) || 0,
        ivaAmount: Number((srcIvaAmt * ratio).toFixed(2)),
        totale: Number(totale.toFixed(2)),
      };
    }
    setIssuingCnId(invoice.id);
    try {
      const cn = await api.issueCreditNote(invoice.id, payload);
      toast.success(
        `Nota di Credito ${cn.invoiceNumber} emessa per € ${(Number(cn.totale) || 0).toFixed(2)}.`
      );
      if (typeof refreshInvoices === "function") {
        try { await refreshInvoices(); } catch { /* swallow */ }
      }
    } catch (err) {
      const msg = (err && err.message) ? err.message : "Errore in emissione Nota di Credito";
      toast.error(msg);
    } finally {
      setIssuingCnId(null);
    }
  };

  // Delete invoice handler with confirmation - receives id and number directly
  const handleDeleteInvoice = async (invoiceId, invoiceNumber) => {
    console.log('[DELETE-A] before confirm, id:', invoiceId, 'number:', invoiceNumber);
    
    const confirmed = window.confirm(
      `Sei sicuro di voler eliminare la fattura ${invoiceNumber || invoiceId}?\n\nQuesta azione non può essere annullata.`
    );
    
    if (!confirmed) {
      console.log('[DELETE-A1] confirm rejected');
      return;
    }
    console.log('[DELETE-B] after confirm accepted');
    
    setDeletingInvoiceId(invoiceId);
    
    // Clear any selected state that might reference this invoice BEFORE deletion
    if (selectedInvoice?.id === invoiceId) {
      console.log('[DELETE-B1] clearing selectedInvoice');
      setSelectedInvoice(null);
    }
    if (pdfViewerInvoice?.id === invoiceId) {
      console.log('[DELETE-B2] closing PDF viewer');
      setPdfViewerOpen(false);
      setPdfViewerInvoice(null);
      setPdfViewerBlob(null);
    }
    
    if (!onDeleteInvoice) {
      console.error('[DELETE-ERR] onDeleteInvoice is undefined');
      setDeletingInvoiceId(null);
      return;
    }
    
    try {
      console.log('[DELETE-C] before onDeleteInvoice');
      await onDeleteInvoice(invoiceId);
      console.log('[DELETE-D] after onDeleteInvoice');
      
      console.log('[DELETE-E] before refreshInvoices, exists:', !!refreshInvoices);
      if (refreshInvoices) {
        await refreshInvoices();
        console.log('[DELETE-F] after refreshInvoices');
      }
      console.log('[DELETE-DONE] delete flow complete');
    } catch (error) {
      console.error('[DELETE-ERR] full error object and stack:', error, error?.stack);
      alert(error.message || "Errore durante l'eliminazione");
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  // PDF handlers - Open PDF in modal viewer (avoids browser popup blocks)
  const handleDownloadPDF = async (invoice) => {
    setDownloadingPdf(invoice.id);
    try {
      // Directly download without opening modal
      await api.downloadInvoicePDF(invoice.id);
    } catch (error) {
      console.error('[PDF] Download error:', error);
      alert(error.message || "Errore durante il download del PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Send invoice via email (mailto + PDF download)
  // Se `template` è passato, si usa il suo subject/body. Altrimenti
  // fallback al primo template default o al legacy /email-template.
  const handleSendEmail = async (invoice, template = null) => {
    const client = clients.find(c => c.id === invoice.clientId);
    const clientEmail = client?.email || client?.pec || "";
    const clientName = client?.fullName || "";
    const studioName = settings?.studioName || "Studio Bonaiuti STP";
    const invDate = formatDate(invoice.invoiceDate);
    const totStr = currency(invoice.totale);

    // Risolvi template. Priorità:
    //   1) template argomento (submenu click)
    //   2) default nella lista emailTemplates
    //   3) API legacy /email-template
    //   4) hardcoded fallback
    const applyPlaceholders = (str) => (str || "")
      .replaceAll("{invoiceNumber}", invoice.invoiceNumber || "")
      .replaceAll("{invoiceDate}", invDate || "")
      .replaceAll("{clientName}", clientName || "")
      .replaceAll("{totale}", totStr || "")
      .replaceAll("{studioName}", studioName || "");

    let subject, body;
    try {
      let tpl = template;
      if (!tpl) {
        // prova il default dalla lista caricata al mount
        tpl = (emailTemplates || []).find((t) => t?.isDefault) || (emailTemplates || [])[0];
      }
      if (!tpl) {
        // legacy fallback
        tpl = await api.getEmailTemplate();
      }
      subject = applyPlaceholders(tpl?.subject || "Fattura n. {invoiceNumber} del {invoiceDate}");
      body = applyPlaceholders(
        tpl?.body ||
          "Gentile {clientName},\n\nIn allegato la fattura n. {invoiceNumber} del {invoiceDate} per un importo di {totale} EUR.\n\nCordiali saluti,\n{studioName}"
      );
    } catch {
      subject = `Fattura n. ${invoice.invoiceNumber} del ${invDate}`;
      body = `Gentile ${clientName},\n\nIn allegato la fattura n. ${invoice.invoiceNumber} del ${invDate} per un importo di ${totStr}.\n\nCordiali saluti,\n${studioName}`;
    }

    // 16/07/2026 — Fix "apre PDF invece di email client":
    // 1) apro PRIMA il mailto (così il client email è protagonista)
    // 2) POI scarico il PDF (i browser non permettono di allegare file
    //    automaticamente ad un mailto: link)
    // 3) mostro un toast per informare l'operatore dove trovare il PDF
    const mailto = `mailto:${encodeURIComponent(clientEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // window.location.href funziona meglio di window.open('mailto', '_blank')
    // per attivare il client email predefinito senza aprire un tab vuoto.
    window.location.href = mailto;

    try {
      await api.downloadInvoicePDF(invoice.id);
      const tplLabel = template?.name ? ` (template "${template.name}")` : "";
      toast.success(`PDF fattura scaricato${tplLabel}. Allegalo manualmente all'email che si è appena aperta.`, { duration: 6000 });
    } catch {
      toast.warning("Email aperta, ma il download automatico del PDF è fallito. Scarica il PDF dal menu ⋯ e allegalo manualmente.");
    }
  };

  // Open PDF in modal for viewing/printing (no popup blocks)
  const handlePrintPDF = async (invoice) => {
    console.log('[PDF] Opening PDF viewer for invoice:', invoice.id);
    setPdfViewerInvoice(invoice);
    setPdfViewerLoading(true);
    setPdfViewerError(null);
    setPdfViewerBlob(null);
    setPdfViewerOpen(true);
    
    try {
      const blob = await api.getInvoicePDFBlob(invoice.id);
      console.log('[PDF] Blob received, size:', blob.size);
      setPdfViewerBlob(blob);
    } catch (error) {
      console.error('[PDF] Error fetching PDF:', error);
      setPdfViewerError(error.message || "Errore durante il caricamento del PDF");
    } finally {
      setPdfViewerLoading(false);
    }
  };
  
  // Close PDF viewer modal
  const closePdfViewer = () => {
    setPdfViewerOpen(false);
    setPdfViewerBlob(null);
    setPdfViewerError(null);
    setPdfViewerInvoice(null);
  };

  // Direct print - renders PDF to images using React state with proper lifecycle
  const handleDirectPrint = async (invoice) => {
    console.log('[PRINT-B] handler entered, invoice.id:', invoice.id);
    
    // Reset abort flag for this print operation
    printAbortRef.current = false;
    
    setPrintingPdf(invoice.id);
    setPrintImages([]);
    setIsPrintReady(false);
    
    let pdfDoc = null;
    
    try {
      // Check if still mounted
      if (!isMountedRef.current || printAbortRef.current) return;
      
      const blob = await api.getInvoicePDFBlob(invoice.id);
      console.log('[PRINT-C] pdf fetched, blob size:', blob.size);
      
      // Check again after async operation
      if (!isMountedRef.current || printAbortRef.current) return;
      
      // Import pdfjs-dist dynamically
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
      
      if (!isMountedRef.current || printAbortRef.current) return;
      
      const arrayBuffer = await blob.arrayBuffer();
      
      if (!isMountedRef.current || printAbortRef.current) return;
      
      pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      if (!isMountedRef.current || printAbortRef.current) {
        pdfDoc.destroy();
        return;
      }
      
      // Render all pages to images using regular canvas (in memory, not DOM)
      const images = [];
      const scale = 2.5;
      
      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        if (!isMountedRef.current || printAbortRef.current) {
          pdfDoc.destroy();
          return;
        }
        
        console.log('[PRINT-D] page render start, page:', pageNum);
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale });
        
        // Create canvas in memory (not attached to DOM)
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext('2d');
        
        if (!context) continue;
        
        await page.render({ canvasContext: context, viewport }).promise;
        console.log('[PRINT-E] page render complete, page:', pageNum);
        
        const dataUrl = canvas.toDataURL('image/png', 1.0);
        images.push(dataUrl);
      }
      
      // Cleanup PDF doc
      pdfDoc.destroy();
      pdfDoc = null;
      
      // Final mount check before setting state
      if (!isMountedRef.current || printAbortRef.current) return;
      
      console.log('[PRINT-F] printImages prepared, count:', images.length);
      
      setPrintImages(images);
      setIsPrintReady(true);
      console.log('[PRINT-G] state set');
      
    } catch (error) {
      // Cleanup on error
      if (pdfDoc) {
        try { pdfDoc.destroy(); } catch (e) { /* ignore */ }
      }
      
      if (!isMountedRef.current || printAbortRef.current) return;
      
      console.error('[PRINT-ERR] full error object and stack:', error, error?.stack);
      alert(error.message || "Errore durante la stampa");
      setPrintingPdf(null);
      setPrintImages([]);
      setIsPrintReady(false);
    }
  };
  
  // useEffect to trigger print when images are ready - with lifecycle guards
  useEffect(() => {
    if (!isPrintReady || printImages.length === 0) return;
    if (!isMountedRef.current) return;
    
    console.log('[PRINT-I] useEffect triggered, images:', printImages.length);
    
    let printTimeout = null;
    let cleanupTimeout = null;
    
    printTimeout = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      console.log('[PRINT-J] before window.print');
      window.print();
      
      cleanupTimeout = setTimeout(() => {
        if (!isMountedRef.current) return;
        
        console.log('[PRINT-K] afterprint cleanup');
        setPrintImages([]);
        setIsPrintReady(false);
        setPrintingPdf(null);
      }, 1000);
    }, 300);
    
    return () => {
      if (printTimeout) clearTimeout(printTimeout);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
    };
  }, [isPrintReady, printImages]);

  // Print invoice list
  const handlePrintList = async () => {
    setIsPrintingList(true);

    // 07/07/2026 P0 extension: gli aggregati manageriali (Imponibile/IVA/
    // Totale/Pagato/Residuo di periodo) NON devono comparire negli export
    // per operatori. Colonne per-riga restano invariate (dati operativi).
    const showAggregates = canViewFinancialDashboard(operatorRole);
    
    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lista Fatture</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 20px; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .text-right { text-align: right; }
          .totals { margin-top: 20px; background: #f9f9f9; padding: 15px; border-radius: 4px; }
          .totals-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 15px; }
          .totals-item { text-align: center; }
          .totals-label { font-size: 10px; color: #666; }
          .totals-value { font-size: 14px; font-weight: bold; margin-top: 4px; }
          .status-badge { padding: 2px 6px; border-radius: 3px; font-size: 10px; }
          .status-pagata { background: #dcfce7; color: #166534; }
          .status-parziale { background: #fef3c7; color: #92400e; }
          .status-non-pagata { background: #fee2e2; color: #991b1b; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Lista Fatture</h1>
        <p class="subtitle">
          ${hasActiveFilters ? "Filtri applicati • " : ""}
          ${filteredInvoices.length} fatture • Stampato il ${new Date().toLocaleDateString('it-IT')}
        </p>
        
        <table>
          <thead>
            <tr>
              <th>Numero</th>
              <th>Gestione</th>
              <th>Data</th>
              <th>Cliente</th>
              <th class="text-right">Imponibile</th>
              <th class="text-right">IVA</th>
              <th class="text-right">Totale</th>
              <th class="text-right">Incassato</th>
              <th class="text-right">Residuo</th>
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            ${filteredInvoices.map(inv => {
              const residuo = (inv.totale || 0) - (inv.totalPaid || 0);
              return `
              <tr>
                <td>${inv.invoiceNumber || '-'}</td>
                <td>${inv.managementType === 'studio' ? 'Studio' : 'Agenzia'}</td>
                <td>${formatDate(inv.invoiceDate)}</td>
                <td>${inv.clientName}</td>
                <td class="text-right">${currency(inv.taxableAmount)}</td>
                <td class="text-right">${currency(inv.ivaAmount)}</td>
                <td class="text-right">${currency(inv.totale)}</td>
                <td class="text-right" style="color: ${(inv.totalPaid || 0) > 0 ? '#166534' : '#999'};">${currency(inv.totalPaid || 0)}</td>
                <td class="text-right" style="color: ${residuo > 0 ? '#dc2626' : '#166534'};">${currency(residuo)}</td>
                <td>
                  <span class="status-badge status-${inv.paymentStatus || 'non-pagata'}">
                    ${inv.paymentStatus === 'pagata' ? 'Pagata' : inv.paymentStatus === 'parziale' ? 'Parziale' : 'Da Pagare'}
                  </span>
                </td>
              </tr>
            `}).join('')}
          </tbody>
        </table>
        
        ${showAggregates ? `
        <div class="totals">
          <div class="totals-grid">
            <div class="totals-item">
              <div class="totals-label">Imponibile</div>
              <div class="totals-value">${currency(totals.imponibile)}</div>
            </div>
            <div class="totals-item">
              <div class="totals-label">IVA</div>
              <div class="totals-value">${currency(totals.iva)}</div>
            </div>
            <div class="totals-item">
              <div class="totals-label">Totale</div>
              <div class="totals-value">${currency(totals.totale)}</div>
            </div>
            <div class="totals-item">
              <div class="totals-label">Pagato</div>
              <div class="totals-value" style="color: #166534;">${currency(totals.pagato)}</div>
            </div>
            <div class="totals-item">
              <div class="totals-label">Residuo</div>
              <div class="totals-value" style="color: #dc2626;">${currency(totals.residuo)}</div>
            </div>
          </div>
        </div>` : ''}
      </body>
      </html>
    `;

    console.log('[PRINT LIST] Opening print preview in new tab...');
    
    // Use window.open for deterministic print behavior
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      console.warn('[PRINT LIST] Popup blocked');
      alert('Popup bloccato. Abilita i popup per stampare la lista.');
      setIsPrintingList(false);
      return;
    }
    
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to render, then trigger print
    printWindow.onload = () => {
      console.log('[PRINT LIST] Content loaded, triggering print...');
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        setIsPrintingList(false);
      }, 500);
    };
    
    // Fallback if onload doesn't fire
    setTimeout(() => {
      if (printWindow && !printWindow.closed) {
        printWindow.focus();
        printWindow.print();
      }
      setIsPrintingList(false);
    }, 2000);
  };

  // Export invoices to Excel - Professional register layout
  const handleExportExcel = useCallback(() => {
    const exportDateStr = new Date().toLocaleDateString('it-IT');
    const gestioneLabel = gestioneFilter === "all" ? "Tutte le Gestioni" : 
                          gestioneFilter === "studio" ? "Gestione Studio" : "Gestione Agenzia";
    
    const numCols = 11;
    const dataHeaders = ["Numero", "Data", "Cliente", "Gestione", "Imponibile", "IVA", "Totale", "Art. 15", "Incassato", "Residuo", "Stato"];
    
    const fmt2dec = (val) => Math.round((Number(val) || 0) * 100) / 100;
    const fmtDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        let d = dateStr;
        if (d.includes('T')) d = d.split('T')[0];
        const parts = d.split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return d;
      } catch { return dateStr; }
    };
    
    const dataRows = filteredInvoices.map(inv => {
      const totalPaid = fmt2dec((inv.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0));
      const remaining = fmt2dec((inv.totale || 0) - totalPaid);
      return [
        inv.invoiceNumber || '',
        fmtDate(inv.invoiceDate),
        inv.clientName || '',
        inv.managementType === 'studio' ? 'Studio' : 'Agenzia',
        fmt2dec(inv.taxableAmount),
        fmt2dec(inv.ivaAmount),
        fmt2dec(inv.totale),
        fmt2dec(inv.speseArt15 || 0),
        totalPaid,
        remaining,
        inv.status || ''
      ];
    });
    
    const emptyRow = Array(numCols).fill("");
    const headerData = [
      ["Registro Fatture", ...Array(numCols - 1).fill("")],
      ["Gruppo Impresa STP", ...Array(numCols - 1).fill("")],
      [`${gestioneLabel}  |  Periodo: dal 01/01/2026 al ${exportDateStr}  |  Esportato il: ${exportDateStr}`, ...Array(numCols - 1).fill("")],
      emptyRow,
      dataHeaders,
      ...dataRows
    ];
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(headerData);
    
    ws['!cols'] = [
      { wch: 16 }, { wch: 14 }, { wch: 32 }, { wch: 12 },
      { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 },
      { wch: 16 }, { wch: 16 }, { wch: 14 },
    ];
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
    ];
    
    applyRegisterStyle(ws, numCols, 4, headerData.length, [4, 5, 6, 7, 8, 9], [0, 1, 3]);
    
    XLSX.utils.book_append_sheet(wb, ws, "Fatture");
    writeFileWithPageSetup(wb, `registro_fatture_${fileDate()}.xlsx`, 88);
  }, [filteredInvoices, gestioneFilter]);

  // Get payment status badge (11/06/2026 — esteso con stati NdC).
  // Una Nota di Credito non è mai "Da Pagare": è un documento di
  // rettifica e non rappresenta un credito da incassare.
  const getPaymentStatusBadge = (invoice, compact = false) => {
    // Credit notes never carry a payment-status badge: they're not receivables.
    if (invoice.documentType === "credit_note") {
      return null;
    }
    const ps = invoice.paymentStatus || "non_pagata";
    const configs = {
      pagata: { className: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle2, label: "Pagata" },
      parziale: { className: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock, label: "Parziale" },
      non_pagata: { className: "bg-red-100 text-red-700 border-red-300", icon: AlertCircle, label: "Da Pagare" },
      stornata: { className: "bg-purple-100 text-purple-700 border-purple-300", icon: ArrowRightLeft, label: "Stornata" },
      parzialmente_stornata: { className: "bg-cyan-100 text-cyan-700 border-cyan-300", icon: ArrowRightLeft, label: "Parz. Stornata" }
    };
    const config = configs[ps] || configs.non_pagata;
    const Icon = config.icon;

    return (
      <Badge className={cn("text-xs", config.className)} title={config.label}>
        <Icon className={cn("h-3 w-3", !compact && "mr-1")} />
        {!compact && config.label}
      </Badge>
    );
  };

  // Get status badge color (11/06/2026 — esteso per Stornata).
  const getStatusBadge = (status) => {
    const configs = {
      Bozza: "bg-slate-100 text-slate-700 border-slate-300",
      Emessa: "bg-blue-100 text-blue-700 border-blue-300",
      Inviata: "bg-purple-100 text-purple-700 border-purple-300",
      Pagata: "bg-green-100 text-green-700 border-green-300",
      Scaduta: "bg-red-100 text-red-700 border-red-300",
      Stornata: "bg-purple-100 text-purple-700 border-purple-300",
      "Parzialmente Stornata": "bg-cyan-100 text-cyan-700 border-cyan-300"
    };
    return (
      <Badge className={cn("text-xs", configs[status] || configs.Bozza)}>
        {status}
      </Badge>
    );
  };

  // If viewing invoice detail, show detail component
  if (viewingInvoiceId) {
    const viewingInvoice = enrichedInvoices.find(inv => inv.id === viewingInvoiceId);
    const viewingClient = viewingInvoice ? clients.find(c => c.id === viewingInvoice.clientId) : null;
    const viewingCase = viewingInvoice?.caseId ? cases.find(c => c.id === viewingInvoice.caseId) : null;
    
    return (
      <InvoiceDetail
        invoice={viewingInvoice}
        client={viewingClient}
        linkedCase={viewingCase}
        onBack={() => setViewingInvoiceId(null)}
        onEdit={() => {
          setViewingInvoiceId(null);
          if (onEditInvoice) onEditInvoice(viewingInvoice);
        }}
        onPrint={() => handlePrintPDF(viewingInvoice)}
        onAddPayment={() => {
          setSelectedInvoice(viewingInvoice);
          setPaymentDialogOpen(true);
        }}
      />
    );
  }

  return (
    <div className="space-y-2" data-testid="fatture-module">
      {/* Header compact — v1.8.0 UX Operativa v2: rimossa Card wrapper
          (recuperati ~40px verticali), stats KPI inline nella stessa
          riga del titolo + azione principale. */}
      <div className="flex items-center justify-between gap-3 px-1 py-1" data-testid="fatture-module-header">
        <div className="flex items-center gap-3">
          <span
            className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "var(--gis-green-50)" }}
          >
            <Receipt className="h-4 w-4" style={{ color: "var(--gis-green)" }} />
          </span>
          <div>
            <h2 className="gis-heading font-bold text-slate-900 text-lg leading-tight">
              {documentType === "proforma" ? "Documenti Proforma" : "Fatture"}
            </h2>
            <p className="text-[11px] text-slate-500 gis-body">
              {filteredInvoices.length} di {invoices.length}
              {hasActiveFilters && " (filtrate)"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {documentType === "proforma" && onCreateProforma && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCreateProforma()}
              data-testid="new-proforma-btn"
              className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              title="Crea un nuovo Documento Proforma"
            >
              <FileText className="mr-1.5 h-4 w-4" /> Nuovo Proforma
            </Button>
          )}
          {documentType === "invoice" && onCreateInvoice && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCreateInvoice()}
              data-testid="new-invoice-btn-inside-fatture"
              className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
              title="Crea una nuova Fattura"
            >
              <Receipt className="mr-1.5 h-4 w-4" /> Crea Fattura
            </Button>
          )}
          <FinancialVisibilityGuard operatorRole={operatorRole}>
            <div className="flex items-center gap-4 border-l border-slate-200 pl-4" data-testid="fatture-financial-summary">
              <div className="text-center">
                <p className="text-[10px] gis-heading-wide text-slate-500 uppercase leading-none">Totale</p>
                <p className="text-sm font-bold text-slate-900 leading-tight mt-0.5">{currency(totals.totale)}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] gis-heading-wide text-slate-500 uppercase leading-none">Incassato</p>
                <p className="text-sm font-bold text-green-600 leading-tight mt-0.5">{currency(totals.pagato)}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] gis-heading-wide text-slate-500 uppercase leading-none">Residuo</p>
                <p className="text-sm font-bold text-amber-600 leading-tight mt-0.5">{currency(totals.residuo)}</p>
              </div>
            </div>
          </FinancialVisibilityGuard>
        </div>
      </div>

      {/* Filters Card */}
      <Card>
        <CardContent className="pt-4">
          {/* Search and Filter Toggle Row */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca per numero, cliente, note..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="invoice-search-input"
              />
            </div>

            {/* Year filter - always visible */}
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-[110px] h-9" data-testid="filter-year">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="2027">2027</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant={showFilters ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
              data-testid="toggle-filters-btn"
            >
              <Filter className="h-4 w-4" />
              Filtri
              {hasActiveFilters && (
                <Badge className="ml-1 bg-blue-600 text-white text-xs px-1.5">
                  {[dateFrom, dateTo, clientFilter !== "all", gestioneFilter !== "all", 
                    paymentStatusFilter !== "all", statusFilter !== "all", amountFrom, amountTo]
                    .filter(Boolean).length}
                </Badge>
              )}
            </Button>

            <div className={cn(hasActiveFilters ? "" : "hidden")}>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            </div>

            <div className="flex-1" />

            {/* Print List Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintList}
              disabled={isPrintingList || filteredInvoices.length === 0}
              className="gap-2"
              data-testid="print-list-btn"
              title={`Stampa ${filteredInvoices.length} righe filtrate`}
            >
              {isPrintingList ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              Stampa Lista
              <span className="ml-1 text-xs opacity-70">({filteredInvoices.length})</span>
            </Button>
            {/* Export Excel Button - single working export */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={filteredInvoices.length === 0}
              className="gap-2"
              data-testid="export-excel-btn"
              title={`Esporta ${filteredInvoices.length} righe filtrate`}
            >
              <Download className="h-4 w-4" />
              Esporta Excel
              <span className="ml-1 text-xs opacity-70">({filteredInvoices.length})</span>
            </Button>
          </div>

          {/* Expanded Filters */}
          <div className={cn(
            "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4 bg-slate-50 rounded-lg border",
            showFilters ? "" : "hidden"
          )}>
            {/* Date From */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Data Da</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9"
                data-testid="filter-date-from"
              />
            </div>

            {/* Date To */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Data A</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9"
                data-testid="filter-date-to"
              />
            </div>

            {/* Client Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Cliente</Label>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="h-9" data-testid="filter-client">
                  <SelectValue placeholder="Tutti i clienti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i clienti</SelectItem>
                  {uniqueClients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Gestione Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Gestione</Label>
              <Select value={gestioneFilter} onValueChange={setGestioneFilter}>
                <SelectTrigger className="h-9" data-testid="filter-gestione">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="studio">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3 w-3" />
                      Studio
                    </div>
                  </SelectItem>
                  <SelectItem value="agenzia">
                    <div className="flex items-center gap-2">
                      <Users className="h-3 w-3" />
                      Agenzia
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Document Type Filter (11/06/2026) — only in invoice mode.
                Allows splitting the unified issued register between
                official invoices and credit notes. */}
            {documentType === "invoice" && (
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Tipo Documento</Label>
                <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
                  <SelectTrigger className="h-9" data-testid="filter-doctype">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="issued">Tutti</SelectItem>
                    <SelectItem value="invoice">Solo Fatture</SelectItem>
                    <SelectItem value="credit_note">Solo Note di Credito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Payment Status Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Stato Pagamento</Label>
              <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
                <SelectTrigger className="h-9" data-testid="filter-payment-status">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="pagata">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                      Pagata
                    </div>
                  </SelectItem>
                  <SelectItem value="parziale">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3 text-amber-600" />
                      Parziale
                    </div>
                  </SelectItem>
                  <SelectItem value="non_pagata">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-3 w-3 text-red-600" />
                      Da Pagare
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Invoice Status Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Stato Fattura</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9" data-testid="filter-status">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="Bozza">Bozza</SelectItem>
                  <SelectItem value="Emessa">Emessa</SelectItem>
                  <SelectItem value="Inviata">Inviata</SelectItem>
                  <SelectItem value="Pagata">Pagata</SelectItem>
                  <SelectItem value="Scaduta">Scaduta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Amount Range */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Importo Da</Label>
              <Input
                type="number"
                placeholder="0"
                value={amountFrom}
                onChange={(e) => setAmountFrom(e.target.value)}
                className="h-9"
                data-testid="filter-amount-from"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Importo A</Label>
              <Input
                type="number"
                placeholder="∞"
                value={amountTo}
                onChange={(e) => setAmountTo(e.target.value)}
                className="h-9"
                data-testid="filter-amount-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="w-[120px] font-semibold">Numero</TableHead>
                  <TableHead className="w-[80px] font-semibold text-center">Gestione</TableHead>
                  <TableHead className="w-[90px] font-semibold">Data</TableHead>
                  <TableHead className="font-semibold">Cliente</TableHead>
                  <TableHead className="w-[95px] font-semibold text-right">Imponibile</TableHead>
                  <TableHead className="w-[70px] font-semibold text-right">IVA</TableHead>
                  <TableHead className="w-[95px] font-semibold text-right">Totale</TableHead>
                  <TableHead className="w-[90px] font-semibold text-right">Incassato</TableHead>
                  <TableHead className="w-[90px] font-semibold text-right">Residuo</TableHead>
                  <TableHead className="w-[90px] font-semibold text-center">Stato</TableHead>
                  <TableHead className="w-[70px] font-semibold text-center">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400">
                        <Receipt className="h-10 w-10 mb-2 opacity-50" />
                        <p>{hasActiveFilters ? "Nessuna fattura con questi filtri" : "Nessuna fattura"}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedInvoices.map((invoice) => {
                    // 11/06/2026 — Residuo per-riga: sottrae anche
                    // l'eventuale importo già stornato da NdC.
                    const residuo = Math.max(
                      0,
                      (invoice.totale || 0)
                        - (invoice.totalPaid || 0)
                        - (invoice.creditedAmount || 0)
                    );
                    
                    // Row click handler - opens invoice detail
                    const handleRowClick = (e) => {
                      // Don't navigate if clicking on buttons or dropdown
                      if (e.target.closest('button') || e.target.closest('[role="menuitem"]')) {
                        return;
                      }
                      setViewingInvoiceId(invoice.id);
                    };
                    
                    return (
                    <TableRow 
                      key={invoice.id} 
                      className="group hover:bg-slate-50 cursor-pointer transition-colors"
                      data-testid={`invoice-row-${invoice.id}`}
                      onClick={handleRowClick}
                    >
                      <TableCell className="font-medium">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            {invoice.invoiceNumber || <span className="text-slate-400 italic">-</span>}
                            {invoice.status === "Bozza" && (
                              <Badge variant="outline" className="text-[10px] px-1">Bozza</Badge>
                            )}
                            {invoice.convertedToInvoiceId && (
                              <Badge variant="outline" className="text-[10px] px-1 border-emerald-300 text-emerald-700 bg-emerald-50">
                                Convertita
                              </Badge>
                            )}
                            {/* Credit Note badge (11/06/2026) */}
                            {invoice.documentType === "credit_note" && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1 border-cyan-300 text-cyan-800 bg-cyan-50"
                                data-testid={`cn-badge-${invoice.id}`}
                                title="Nota di Credito"
                              >
                                Nota di Credito
                              </Badge>
                            )}
                            {/* Invoice flagged as having credit notes
                                issued against it (11/06/2026) */}
                            {(!invoice.documentType || invoice.documentType === "invoice")
                              && Array.isArray(invoice.creditNoteIds)
                              && invoice.creditNoteIds.length > 0 && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1 border-amber-300 text-amber-800 bg-amber-50"
                                title={`${invoice.creditNoteIds.length} Nota/e di Credito collegata/e`}
                              >
                                {invoice.creditNoteIds.length} NdC
                              </Badge>
                            )}
                          </div>
                          {/* Cross-reference labels (28/02/2026): visibile
                              su entrambi i lati della conversione per
                              tracciabilità documentale. */}
                          {invoice.convertedToInvoiceNumber && (
                            <span
                              className="text-[10px] text-emerald-700"
                              data-testid={`converted-to-${invoice.id}`}
                              title="Numero fattura ufficiale generata"
                            >
                              → Fattura {invoice.convertedToInvoiceNumber}
                            </span>
                          )}
                          {invoice.sourceProformaNumber && (
                            <span
                              className="text-[10px] text-slate-500"
                              data-testid={`from-proforma-${invoice.id}`}
                              title="Proforma sorgente"
                            >
                              Da Proforma {invoice.sourceProformaNumber}
                            </span>
                          )}
                          {/* Credit-note source reference (11/06/2026) */}
                          {invoice.sourceInvoiceNumber && invoice.documentType === "credit_note" && (
                            <span
                              className="text-[10px] text-cyan-700"
                              data-testid={`from-invoice-${invoice.id}`}
                              title="Fattura sorgente stornata"
                            >
                              Storno Fattura {invoice.sourceInvoiceNumber}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-[10px]",
                            invoice.managementType === "studio" 
                              ? "border-blue-300 text-blue-700" 
                              : "border-purple-300 text-purple-700"
                          )}
                        >
                          {invoice.managementType === "studio" ? "S" : "A"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {formatDate(invoice.invoiceDate)}
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate max-w-[180px] block cursor-help text-sm">
                                {invoice.clientName}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{invoice.clientName}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {currency(invoice.taxableAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-slate-500">
                        {currency(invoice.ivaAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">
                        {currency(invoice.totale)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn(
                          "font-mono text-sm",
                          (invoice.totalPaid || 0) > 0 ? "text-green-600 font-medium" : "text-slate-400"
                        )}>
                          {invoice.documentType === "credit_note"
                            ? <span className="text-slate-400" title="N/A per Nota di Credito">—</span>
                            : currency(invoice.totalPaid || 0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn(
                          "font-mono text-sm font-medium",
                          residuo > 0 ? "text-amber-600" : "text-green-600"
                        )}>
                          {invoice.documentType === "credit_note"
                            ? <span className="text-slate-400" title="N/A per Nota di Credito">—</span>
                            : currency(residuo)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {getPaymentStatusBadge(invoice)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {/* Quick payment button - always visible */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={cn(
                                    "h-8 w-8 p-0",
                                    invoice.paymentStatus === "pagata" 
                                      ? "text-green-600 hover:text-green-700" 
                                      : "text-blue-600 hover:text-blue-700"
                                  )}
                                  onClick={() => openPaymentDialog(invoice)}
                                  data-testid={`quick-payment-${invoice.id}`}
                                >
                                  <Euro className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{invoice.paymentStatus === "pagata" ? "Vedi Pagamenti" : "Registra Pagamento"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {/* Quick PDF download */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
                                  onClick={() => handleDownloadPDF(invoice)}
                                  disabled={downloadingPdf === invoice.id}
                                  data-testid={`quick-pdf-${invoice.id}`}
                                >
                                  {downloadingPdf === invoice.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Scarica PDF</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`invoice-actions-${invoice.id}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {/* Payments */}
                              <DropdownMenuItem 
                                onClick={() => openPaymentDialog(invoice)}
                                className="gap-2"
                              >
                                <Euro className="h-4 w-4" />
                                {invoice.paymentStatus === "pagata" ? "Vedi Pagamenti" : "Gestisci Pagamenti"}
                              </DropdownMenuItem>

                              {/* Edit */}
                              <DropdownMenuItem 
                                onClick={() => onEditInvoice(invoice)}
                                className="gap-2"
                              >
                                <Pencil className="h-4 w-4" />
                                Modifica
                              </DropdownMenuItem>

                              {/* Converti proforma in fattura ufficiale
                                  (28/02/2026): visibile solo per
                                  proforma non ancora convertite. */}
                              {documentType === "proforma" && !invoice.convertedToInvoiceId && (
                                <DropdownMenuItem
                                  onClick={() => handleConvertToInvoice(invoice)}
                                  disabled={convertingId === invoice.id}
                                  className="gap-2 text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50"
                                  data-testid={`convert-proforma-${invoice.id}`}
                                >
                                  {convertingId === invoice.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <ArrowRightLeft className="h-4 w-4" />
                                  )}
                                  Converti in Fattura
                                </DropdownMenuItem>
                              )}

                              {/* Emetti Nota di Credito (11/06/2026):
                                  visibile solo per fatture ufficiali
                                  (no proforma, no NdC) e solo se
                                  esiste ancora margine stornabile
                                  (effectiveDueAmount > 0 oppure totale
                                  > creditedAmount). */}
                              {documentType === "invoice"
                                && (!invoice.documentType || invoice.documentType === "invoice")
                                && ((Number(invoice.totale) || 0) - (Number(invoice.creditedAmount) || 0)) > 0.01 && (
                                <DropdownMenuItem
                                  onClick={() => handleIssueCreditNote(invoice)}
                                  disabled={issuingCnId === invoice.id}
                                  className="gap-2 text-cyan-700 focus:text-cyan-800 focus:bg-cyan-50"
                                  data-testid={`issue-cn-${invoice.id}`}
                                >
                                  {issuingCnId === invoice.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <FileText className="h-4 w-4" />
                                  )}
                                  Emetti Nota di Credito
                                </DropdownMenuItem>
                              )}

                              {/* Duplica Fattura (11/06/2026): disponibile
                                  per tutte le fatture ufficiali (no NdC
                                  duplicabili). Crea una nuova bozza con
                                  cliente, righe, IVA, split payment,
                                  Art.15, bollo, metodo di pagamento e
                                  note clonati; numero/data assegnati al
                                  salvataggio dal counter ufficiale. */}
                              {documentType === "invoice"
                                && (!invoice.documentType || invoice.documentType === "invoice")
                                && typeof onDuplicateInvoice === "function" && (
                                <DropdownMenuItem
                                  onClick={() => onDuplicateInvoice(invoice)}
                                  className="gap-2 text-indigo-700 focus:text-indigo-800 focus:bg-indigo-50"
                                  data-testid={`duplicate-invoice-${invoice.id}`}
                                >
                                  <Copy className="h-4 w-4" />
                                  Duplica Fattura
                                </DropdownMenuItem>
                              )}

                              <DropdownMenuSeparator />

                              {/* View PDF in modal */}
                              <DropdownMenuItem 
                                onClick={() => handlePrintPDF(invoice)}
                                className="gap-2"
                              >
                                <Eye className="h-4 w-4" />
                                Visualizza PDF
                              </DropdownMenuItem>

                              {/* Invia email — submenu con template disponibili.
                                  Click sul trigger stesso (senza sotto-click)
                                  usa il template default (v1.4.1). */}
                              {emailTemplates.length > 1 ? (
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger
                                    className="gap-2"
                                    data-testid={`send-email-${invoice.id}`}
                                  >
                                    <Mail className="h-4 w-4" />
                                    Invia fattura via email
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    {emailTemplates.map((tpl) => (
                                      <DropdownMenuItem
                                        key={tpl.id}
                                        onClick={() => handleSendEmail(invoice, tpl)}
                                        className="gap-2"
                                        data-testid={`send-email-tpl-${invoice.id}-${tpl.id}`}
                                      >
                                        <Mail className="h-4 w-4 text-orange-600" />
                                        <span className="flex-1">{tpl.name}</span>
                                        {tpl.isDefault && (
                                          <span className="text-[9px] uppercase tracking-wide text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded border border-orange-200 ml-1">
                                            default
                                          </span>
                                        )}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => handleSendEmail(invoice)}
                                  className="gap-2"
                                  data-testid={`send-email-${invoice.id}`}
                                >
                                  <Mail className="h-4 w-4" />
                                  Invia fattura via email
                                </DropdownMenuItem>
                              )}

                              <DropdownMenuItem 
                                onClick={() => exportInvoiceXML(invoice)}
                                className="gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                Esporta XML
                              </DropdownMenuItem>

                              {/* Delete action */}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => {
                                  console.log('[DELETE-0] menu clicked, invoice.id:', invoice.id);
                                  handleDeleteInvoice(invoice.id, invoice.invoiceNumber);
                                }}
                                disabled={deletingInvoiceId === invoice.id}
                                className="gap-2 text-red-600 focus:text-red-600 focus:bg-red-50"
                                data-testid={`delete-invoice-${invoice.id}`}
                              >
                                {deletingInvoiceId === invoice.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                                Elimina Fattura
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
            {invoicesPaginationBar}
          </div>

          {/* Table Footer with Totals */}
          {/* Footer aggregato — mostra Imponibile/IVA/Totale/Pagato/Residuo.
              Guardato da FinancialVisibilityGuard (07/07/2026 P0):
              solo admin/responsabile lo vedono. Per operatori
              rimane solo il conteggio delle righe (metadato operativo). */}
          <FinancialVisibilityGuard operatorRole={operatorRole}>
            <div className={cn(
              "border-t bg-slate-50 p-4",
              filteredInvoices.length === 0 && "hidden"
            )} data-testid="fatture-footer-totals">
              <div className="flex items-center justify-end gap-6 text-sm">
                <div className="text-slate-600">
                  <span className="font-medium">{filteredInvoices.length}</span> fatture
                </div>
                <Separator orientation="vertical" className="h-6" />
                <div>
                  <span className="text-slate-500">Imponibile:</span>
                  <span className="ml-2 font-semibold">{currency(totals.imponibile)}</span>
                </div>
                <div>
                  <span className="text-slate-500">IVA:</span>
                  <span className="ml-2 font-semibold">{currency(totals.iva)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Totale:</span>
                  <span className="ml-2 font-bold text-slate-900">{currency(totals.totale)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Pagato:</span>
                  <span className="ml-2 font-bold text-green-600">{currency(totals.pagato)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Residuo:</span>
                  <span className="ml-2 font-bold text-amber-600">{currency(totals.residuo)}</span>
                </div>
              </div>
            </div>
          </FinancialVisibilityGuard>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        invoice={selectedInvoice}
        onAddPayment={handleAddPayment}
        onRemovePayment={handleRemovePayment}
      />
      
      {/* PDF Viewer Modal (for print/view from Actions menu) */}
      <PDFPreviewModal
        isOpen={pdfViewerOpen}
        onClose={closePdfViewer}
        pdfBlob={pdfViewerBlob}
        isLoading={pdfViewerLoading}
        error={pdfViewerError}
        title={pdfViewerInvoice ? `Fattura ${pdfViewerInvoice.invoiceNumber}` : "Visualizza PDF"}
      />
      
      {/* Print Content - React-managed, hidden on screen, visible only during print */}
      {isPrintReady && printImages.length > 0 && (
        <>
          <style>
            {`
              @media print {
                body > *:not(#react-print-container) { display: none !important; }
                #react-print-container { 
                  display: block !important; 
                  position: static !important;
                }
                #react-print-container img {
                  max-width: 100% !important;
                  height: auto !important;
                  page-break-after: always;
                }
                #react-print-container img:last-child {
                  page-break-after: avoid;
                }
              }
              @media screen {
                #react-print-container {
                  display: none !important;
                }
              }
            `}
          </style>
          <div id="react-print-container">
            {printImages.map((src, index) => (
              <img 
                key={index} 
                src={src} 
                alt={`Page ${index + 1}`}
                style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
